<script setup lang="ts">
import type { GroupedTableColumn, GroupedTableGroup } from "~~/types";
import type { ShipCompareShip } from "~~/types/shipexkurs";
import type { ShipCompareBadge, ShipCompareRow } from "~/utils/ship-compare-metrics";

const props = defineProps<{ ships: ShipCompareShip[] }>();

interface RenderCell {
  value: number | null;
  text: string | null;
  badges: ShipCompareBadge[];
}

interface RenderRow extends ShipCompareRow {
  cells: Record<string, RenderCell>;
  bestShipId: string | null;
  worstShipId: string | null;
}

const LABEL_COLUMN_KEY = "__label";

const columns = computed<GroupedTableColumn[]>(() => [
  {
    key: LABEL_COLUMN_KEY,
    label: "",
    headerClass: "sticky left-0 z-20 min-w-48 w-48 bg-elevated/60",
    cellClass: "sticky left-0 z-10 min-w-48 w-48 bg-bunker-950",
  },
  ...props.ships.map((ship): GroupedTableColumn => ({
    key: ship.id,
    label: ship.summary.name,
    headerClass: "min-w-72 w-72",
    cellClass: "min-w-72 w-72",
    meta: { color: ship.color, pending: ship.rawPending },
  })),
]);

/** Bester/schlechtester Wert je Zeile — nur bei `direction !== 'none'` und mind. 2 unterschiedlichen Werten. */
function findExtremes(row: ShipCompareRow, cells: Record<string, RenderCell>): { best: string | null; worst: string | null } {
  if (row.direction === "none") return { best: null, worst: null };

  const entries = Object.entries(cells)
    .map(([id, cell]) => [id, cell.value] as const)
    .filter((entry): entry is [string, number] => entry[1] != null);

  if (entries.length < 2) return { best: null, worst: null };

  const sorted = [...entries].sort(([, a], [, b]) => (row.direction === "higher" ? b - a : a - b));
  const best = sorted[0]![0];
  const worst = sorted[sorted.length - 1]![0];
  return best === worst ? { best: null, worst: null } : { best, worst };
}

const groups = computed<GroupedTableGroup<RenderRow>[]>(() =>
  SHIP_COMPARE_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    rows: group.rows.map((row): RenderRow => {
      const cells: Record<string, RenderCell> = {};
      for (const ship of props.ships) {
        cells[ship.id] = {
          value: row.value ? row.value(ship.summary, ship.raw) : null,
          text: row.text ? row.text(ship.summary, ship.raw) : null,
          badges: row.badges ? row.badges(ship.summary, ship.raw) : [],
        };
      }
      const { best, worst } = findExtremes(row, cells);
      return { ...row, cells, bestShipId: best, worstShipId: worst };
    }),
  })),
);

/** Balkenbreite = Anteil am besten Wert der Zeile (bei `direction:'lower'` invertiert am niedrigsten Wert). */
function barPercent(row: RenderRow, value: number): number {
  const values = Object.values(row.cells)
    .map((cell) => cell.value)
    .filter((entry): entry is number => entry != null);
  if (!values.length) return 0;

  if (row.direction === "lower") {
    const min = Math.min(...values);
    if (value <= 0) return min <= 0 ? 100 : 0;
    return Math.min(100, (min / value) * 100);
  }

  const max = Math.max(...values);
  if (max <= 0) return 0;
  return Math.min(100, (value / max) * 100);
}

function colorFor(shipId: string): string {
  return props.ships.find((ship) => ship.id === shipId)?.color ?? "var(--color-aris-400)";
}
</script>

<template>
  <UiGroupedTable
    :columns="columns"
    :groups="groups"
    table-class="min-w-full min-w-max"
    empty-text="Keine Schiffe ausgewählt."
  >
    <template #cell="{ row, column }">
      <template v-if="column.key === LABEL_COLUMN_KEY">
        <span class="text-sm text-neutral-300">{{ row.label }}</span>
      </template>

      <template v-else-if="column.meta?.pending">
        <UIcon name="i-lucide-loader-2" class="size-4 animate-spin text-neutral-500" />
      </template>

      <template v-else-if="row.renderAs === 'text'">
        <span class="text-sm text-highlighted">{{ row.cells[column.key]?.text ?? "–" }}</span>
      </template>

      <template v-else-if="row.renderAs === 'badges'">
        <div v-if="row.cells[column.key]?.badges.length" class="flex flex-wrap gap-1">
          <UBadge
            v-for="badge in row.cells[column.key]!.badges"
            :key="badge.label"
            size="sm"
            variant="subtle"
            color="neutral"
          >
            {{ badge.label }} {{ badge.value }}
          </UBadge>
        </div>
        <span v-else class="text-xs text-dimmed">–</span>
      </template>

      <template v-else-if="row.cells[column.key]?.value != null">
        <div class="mb-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <div
            class="h-full rounded-full transition-all"
            :class="row.bestShipId === column.key ? 'shadow-[0_0_6px_currentColor]' : undefined"
            :style="{ width: `${barPercent(row, row.cells[column.key]!.value!)}%`, backgroundColor: colorFor(column.key) }"
          />
        </div>
        <span
          v-if="row.bestShipId === column.key"
          class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-sm font-bold"
          title="Bester Wert der Zeile"
          :style="{
            color: colorFor(column.key),
            backgroundColor: `color-mix(in srgb, ${colorFor(column.key)} 18%, transparent)`,
            boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${colorFor(column.key)} 55%, transparent)`,
          }"
        >
          <UIcon name="i-lucide-crown" class="size-3.5 shrink-0" />
          {{ formatShipCompareValue(row, row.cells[column.key]!.value!) }}
        </span>
        <span
          v-else
          class="text-sm"
          :class="row.worstShipId === column.key ? 'text-neutral-500' : 'font-medium text-highlighted'"
          :title="row.worstShipId === column.key ? 'Schlechtester Wert der Zeile' : undefined"
        >
          {{ formatShipCompareValue(row, row.cells[column.key]!.value!) }}
        </span>
      </template>

      <span v-else class="text-xs text-dimmed">–</span>
    </template>
  </UiGroupedTable>
</template>
